import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import React, { useState } from 'react';
import PracticeCharacterizationGraphTooltip from './PracticeCharacterizationGraphTooltip';
import { FILTER_TYPES_KEYS } from '../../../constants/appraisal-comparison-filter-types';
import constants from '@shared-styles/constants.module.scss';
import NotEnoughDataMessage from '../../shared/components/NotEnoughDataMessage';
import { getXAxisLabel, getYAxisLabel } from '../../shared/components/GraphLabels';
import { itemSorterFn } from '../../../utils/chartUtils';

const LEGEND_ORDER = {
  SatisfiedInd: 1,
  PartialInd: 2,
  DeficientInd: 3,
  SatisfiedOrg: 4,
  PartialOrg: 5,
  DeficientOrg: 6
};

const buildPracticeData = practiceArea => {
  if (!practiceArea || !practiceArea.Practices) {
    return [];
  }

  const data = [...practiceArea.Practices];
  data.sort((a, b) => a.Level - b.Level || a.NumberInLevel - b.NumberInLevel);
  return data.map(practice => ({
    ...practice,
    Acronym: practiceArea.Acronym
  }));
};

const scaleOrgDataToMaxHeight = (data, scaleCoefficient) => {
  return data.map(data => ({
    ...data,
    SatisfiedOrg: data.SatisfiedOrg * scaleCoefficient,
    PartialOrg: data.PartialOrg * scaleCoefficient,
    DeficientOrg: data.DeficientOrg * scaleCoefficient
  }));
};

const yAxisTickFormatter = (tick, yAxisMaxHeight) => {
  if (tick == 0) {
    return `0%`;
  } else if (tick == (yAxisMaxHeight * 25) / 100) {
    return `25%`;
  } else if (tick == (yAxisMaxHeight * 50) / 100) {
    return `50%`;
  } else if (tick == (yAxisMaxHeight * 75) / 100) {
    return `75%`;
  } else if (tick == yAxisMaxHeight) {
    return `100%`;
  } else {
    return `${tick}%`;
  }
};

/**
 * @typedef {object} PracticeCharacterizationOrgVsIndGraphProps
 * @property {Object} [practiceArea]
 * @property {(data: import('recharts').BarRectangleItem) => void} [practiceClick]
 */

/**
 * @param {PracticeCharacterizationOrgVsIndGraphProps} props
 */
const PracticeCharacterizationOrgVsIndGraph = props => {
  const { practiceClick, practiceArea = {} } = props;

  const [activeBarType, setActiveBarType] = useState('');
  let data = buildPracticeData(practiceArea);
  const totalData = Math.max(
    ...data.map(practice => practice.SatisfiedOrg + practice.PartialOrg + practice.DeficientOrg)
  );
  const yAxisMaxHeight = Math.max(
    ...data.map(practice => practice.SatisfiedInd + practice.PartialInd + practice.DeficientInd)
  );
  data = scaleOrgDataToMaxHeight(data, yAxisMaxHeight / totalData);

  return (
    <div onClick={e => e.stopPropagation()} className='practice-characterization-graph-org-vs-ind'>
      {totalData <= 2 ? <NotEnoughDataMessage /> : null}
      <ResponsiveContainer width='95%' height={400}>
        <ComposedChart
          data={data}
          barCategoryGap='25%'
          margin={{
            top: 20,
            right: 20,
            bottom: 20,
            left: 5
          }}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis dataKey='Name'>{getXAxisLabel('Practices within PA')}</XAxis>
          {totalData >= 3 && (
            <Tooltip
              content={<PracticeCharacterizationGraphTooltip activeBarType={activeBarType} />}
            />
          )}
          <YAxis
            domain={[0, yAxisMaxHeight]}
            ticks={[
              0,
              (yAxisMaxHeight * 25) / 100,
              (yAxisMaxHeight * 50) / 100,
              (yAxisMaxHeight * 75) / 100,
              yAxisMaxHeight
            ]}
            tickFormatter={tick => yAxisTickFormatter(tick, yAxisMaxHeight)}>
            {getYAxisLabel('# Appraisals containing PA', 0, 5)}
          </YAxis>
          <Legend
            wrapperStyle={{
              marginLeft: '10%',
              width: '80%',
              bottom: '35px',
              paddingTop: '2.5rem'
            }}
            align='center'
            itemSorter={itemSorterFn(LEGEND_ORDER)}
          />
          {totalData >= 3 && (
            <Bar
              dataKey='SatisfiedInd'
              name='Ind Satisfied'
              stackId='a'
              fill={constants.practiceSatisfiedColor}
              cursor='pointer'
              onClick={data => practiceClick(data)}
              onMouseEnter={() => setActiveBarType(FILTER_TYPES_KEYS.Industry)}
              onMouseLeave={() => setActiveBarType('')}
            />
          )}
          {totalData >= 3 && (
            <Bar
              dataKey='PartialInd'
              name='Ind Partial'
              stackId='a'
              fill={constants.practicePartialColor}
              cursor='pointer'
              onClick={data => practiceClick(data)}
              onMouseEnter={() => setActiveBarType(FILTER_TYPES_KEYS.Industry)}
              onMouseLeave={() => setActiveBarType('')}
            />
          )}
          {totalData >= 3 && (
            <Bar
              dataKey='DeficientInd'
              name='Ind Deficient'
              stackId='a'
              fill={constants.practiceDeficientColor}
              cursor='pointer'
              onClick={data => practiceClick(data)}
              onMouseEnter={() => setActiveBarType(FILTER_TYPES_KEYS.Industry)}
              onMouseLeave={() => setActiveBarType('')}
            />
          )}
          {totalData >= 3 && (
            <Bar
              dataKey='SatisfiedOrg'
              name='Org Satisfied'
              stackId='b'
              stroke={constants.characterizationOrgAndUnitColor}
              strokeWidth={2}
              fill={constants.practiceSatisfiedColor}
              cursor='pointer'
              onClick={data => practiceClick(data)}
              onMouseEnter={() => setActiveBarType(FILTER_TYPES_KEYS.Organization)}
              onMouseLeave={() => setActiveBarType('')}
            />
          )}
          {totalData >= 3 && (
            <Bar
              dataKey='PartialOrg'
              name='Org Partial'
              stackId='b'
              stroke={constants.characterizationOrgAndUnitColor}
              strokeWidth={2}
              fill={constants.practicePartialColor}
              cursor='pointer'
              onClick={data => practiceClick(data)}
              onMouseEnter={() => setActiveBarType(FILTER_TYPES_KEYS.Organization)}
              onMouseLeave={() => setActiveBarType('')}
            />
          )}
          {totalData >= 3 && (
            <Bar
              dataKey='DeficientOrg'
              name='Org Deficient'
              stackId='b'
              stroke={constants.characterizationOrgAndUnitColor}
              strokeWidth={2}
              fill={constants.practiceDeficientColor}
              cursor='pointer'
              onClick={data => practiceClick(data)}
              onMouseEnter={() => setActiveBarType(FILTER_TYPES_KEYS.Organization)}
              onMouseLeave={() => setActiveBarType('')}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PracticeCharacterizationOrgVsIndGraph;
